import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'
import { Button } from '../ui/button'
import { WorkoutResultForm } from '../training-diary/WorkoutResultForm'
import { workoutResultsService } from '../../services/workout-results'
import { useTimerStore } from '../../stores/timer-store'
import type { WorkoutResultPayload } from '../../types/workout'

export function EndWorkoutModal() {
  const { t } = useTranslation()
  const open = useTimerStore((s) => s.endModalOpen)
  const closeEndModal = useTimerStore((s) => s.closeEndModal)
  const reset = useTimerStore((s) => s.reset)
  const discard = useTimerStore((s) => s.discard)
  const exercise = useTimerStore((s) => s.exercise)

  const [error, setError] = useState<string | null>(null)
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false)

  async function handleSave(payload: WorkoutResultPayload) {
    if (!exercise || !exercise.id) return
    setError(null)
    try {
      await workoutResultsService.create({ ...payload, exercise_id: exercise.id })
      reset()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Save failed'
      setError(message)
    }
  }

  function handleDiscardConfirmed() {
    setDiscardConfirmOpen(false)
    discard()
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) closeEndModal() }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('workoutTimer.endModal.title', 'Record workout result')}</DialogTitle>
            <DialogDescription>
              {t('workoutTimer.endModal.description', 'Save how this session went for {{title}}', {
                title: exercise?.title ?? '',
              })}
            </DialogDescription>
          </DialogHeader>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <WorkoutResultForm onSave={handleSave} onCancel={closeEndModal} />
          <div className="flex justify-center pt-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => setDiscardConfirmOpen(true)}
            >
              {t('workoutTimer.endModal.discardButton', 'Discard without saving')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={discardConfirmOpen} onOpenChange={setDiscardConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('workoutTimer.endModal.discardTitle', 'Discard this session?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'workoutTimer.endModal.discardDescription',
                'You will lose the progress of this timer session. The workout itself stays scheduled.',
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDiscardConfirmed}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('workoutTimer.endModal.discardConfirm', 'Discard')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
